import React from 'react'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import WebAssetIcon from '@mui/icons-material/WebAsset'
import { Typography } from '@mui/material'
import { differenceInWeeks, intervalToDuration } from 'date-fns'

import Box from 'components/Common/Box'
import ColorBox from 'components/Common/ColorBox'
import CopyToClipboard from 'components/Common/CopyToClipboard'
import ExtendedAvatar from 'components/Common/ExtendedAvatar'
import CertifiedTag from 'components/Inventory/Common/CertifiedTag'

import { textFiles } from 'constants/textFiles'
import useSetting from 'hooks/useSetting'
import useTranslation from 'hooks/useTranslation'
import { InventoryCar } from 'models/services/inventory/detail'

import placeholderCar from 'images/blueDefaultAvatar.png'

import { colors, FONT_WEIGHT } from 'styles/theme'

import {
  ImgContainer,
  InformationBox,
  StyledBrandImg,
  StyledContainer,
  StyledDataText,
  StyledDataTitle,
  StyledLink,
  VehicleBox,
} from './style'

type VehicleSummaryProps = {
  car: InventoryCar

  /**
   * Boolean added to check if this detail page was called through Preinventory listing
   * or inventoryListing
   */
  comesFromInventoryListingPage?: boolean
}

const VehicleSummary = ({
  car,
  comesFromInventoryListingPage,
}: VehicleSummaryProps) => {
  const {
    text: { vehicleSummary: translation },
  } = useTranslation(textFiles.INVENTORY_DETAIL)

  const appSetting = useSetting()[2]

  const {
    brand,
    categories,
    carModel,
    countryVersion,
    exteriorColor,
    interiorColor,
    mainPicture,
    slug,
    trimLevel,
    year,
    certified,
    publishedAt,
  } = car

  let { days: dayInterval } = intervalToDuration({
    start: new Date(publishedAt),
    end: new Date(),
  })

  if (dayInterval && dayInterval > 7) {
    dayInterval -= Math.floor(dayInterval / 7) * 7
  }

  const weekInterval = differenceInWeeks(new Date(), new Date(publishedAt))

  return (
    <VehicleBox>
      <ImgContainer
        title={translation.vehicleThumbnailTitle}
        description={translation.vehicleThumbnailDescription}
        contentStyle={{
          height: '100%',
        }}
      >
        <Box>
          <section
            style={{ height: '100%', position: 'relative', width: '100%' }}
          >
            <Box
              display="flex"
              borderRadius="10px"
              padding="1rem"
              sx={{
                height: '365px',
                width: '365px',
                border: `1px solid ${colors.border}`,
              }}
            >
              <ExtendedAvatar
                image={mainPicture || placeholderCar}
                variant="rounded"
                height="100%"
                width="100%"
                sx={{
                  height: '100%',
                  width: '100%',
                }}
              />
            </Box>
          </section>
        </Box>
      </ImgContainer>
      <Box>
        <InformationBox>
          <StyledContainer
            title={translation.basicInformationTitle}
            description={translation.basicInformationDescription}
            contentStyle={{
              height: '100%',
            }}
            sx={{
              width: '350px',
            }}
          >
            <Box
              border={`1px solid ${colors.border}`}
              borderRadius="10px"
              padding="1rem"
            >
              <Box
                display="flex"
                flexDirection="column"
                borderRadius="10px"
                padding="2rem"
                sx={{ backgroundColor: colors.background, height: '465px' }}
              >
                {brand.icon && (
                  <StyledBrandImg
                    src={brand.icon}
                    srcSet={brand.icon}
                    alt="brand logo"
                    loading="lazy"
                  />
                )}
                <Box>
                  <StyledDataText variant="h5">{brand.name}</StyledDataText>
                  <StyledDataTitle variant="body1">
                    {translation.makeLabel}
                  </StyledDataTitle>
                </Box>
                <Box marginTop="1.5rem">
                  <StyledDataText variant="h5">{carModel.name}</StyledDataText>
                  <StyledDataTitle variant="body1">
                    {translation.modelLabel}
                  </StyledDataTitle>
                </Box>
                <Box marginTop="1.5rem">
                  <StyledDataText variant="h5">{year}</StyledDataText>
                  <StyledDataTitle variant="body1">
                    {translation.yearLabel}
                  </StyledDataTitle>
                </Box>
                <Box marginTop="1.5rem">
                  <StyledDataText variant="h5">{trimLevel.name}</StyledDataText>
                  <StyledDataTitle variant="body1">
                    {translation.trimLabel}
                  </StyledDataTitle>
                </Box>
              </Box>
            </Box>
          </StyledContainer>
          <StyledContainer
            title={translation.catalogTitle}
            description={translation.catalogDescription}
            contentStyle={{
              height: '100%',
            }}
            sx={{
              width: '350px',
            }}
          >
            <Box
              border={`1px solid ${colors.border}`}
              borderRadius="10px"
              padding="1rem"
            >
              <Box
                display="flex"
                flexDirection="column"
                borderRadius="10px"
                padding="2rem"
                sx={{ backgroundColor: colors.background, height: '465px' }}
              >
                {categories.length > 0 ? (
                  <Box>
                    <StyledDataText variant="h5">
                      {categories.map((category) => category.name).join(', ')}
                    </StyledDataText>
                    <StyledDataTitle variant="body1">
                      {translation.categoriesLabel}
                    </StyledDataTitle>
                  </Box>
                ) : null}
                <Box marginTop="1.5rem">
                  <StyledDataText variant="h5">
                    {countryVersion.name}
                  </StyledDataText>
                  <StyledDataTitle variant="body1">
                    {translation.countryVersionLabel}
                  </StyledDataTitle>
                </Box>
                <Box marginTop="1.5rem">
                  <Box alignItems="center" display="flex">
                    <ColorBox
                      height={27}
                      width={27}
                      styles={{
                        border: `1px solid ${colors.gray}`,
                        boxShadow: 'none',
                      }}
                      hexCode={exteriorColor.hexCode}
                    />
                    <StyledDataText marginLeft="1rem" variant="h5">
                      {exteriorColor.name}
                    </StyledDataText>
                  </Box>

                  <StyledDataTitle variant="body1">
                    {translation.exteriorColorLabel}
                  </StyledDataTitle>
                </Box>
                <Box marginTop="1.5rem">
                  <Box alignItems="center" display="flex">
                    <ColorBox
                      height={27}
                      width={27}
                      styles={{
                        border: `1px solid ${colors.gray}`,
                        boxShadow: 'none',
                      }}
                      hexCode={interiorColor.hexCode}
                    />
                    <StyledDataText marginLeft="1rem" variant="h5">
                      {interiorColor.name}
                    </StyledDataText>
                  </Box>

                  <StyledDataTitle variant="body1">
                    {translation.interiorColorLabel}
                  </StyledDataTitle>
                </Box>
                {comesFromInventoryListingPage ? (
                  <Box marginTop="1.5rem">
                    <StyledDataText variant="h5">
                      {`${dayInterval} ${translation.days}`}
                      {weekInterval
                        ? `, ${weekInterval} ${translation.weeks}`
                        : null}
                    </StyledDataText>
                    <StyledDataTitle variant="body1">
                      {translation.timeOnInventory}
                    </StyledDataTitle>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </StyledContainer>
        </InformationBox>
        <Box marginTop="1.5rem">
          <Box display="flex" justifyContent="flex-start">
            <Box display="flex">
              <DirectionsCarIcon
                fontSize="medium"
                sx={{ color: colors.black }}
              />
              <Typography
                variant="body2"
                color={colors.black}
                margin="0 1rem 0 0.25rem"
                fontWeight={FONT_WEIGHT.BOLD}
              >
                {'Slug: '}
                <CopyToClipboard color={colors.blue} textToCopy={slug} />
              </Typography>
            </Box>
            {/* <Box display="flex">
              <PersonIcon fontSize="medium" sx={{ color: colors.black }} />
              <Typography
                variant="body2"
                color={colors.black}
                margin="0 1rem 0 0.25rem"
                fontWeight={FONT_WEIGHT.BOLD}
              >
                {translation.createdByLabel}{' '}
                <span
                  style={{
                    color: colors.blue,
                    fontWeight: FONT_WEIGHT.MEDIUM,
                  }}
                >
                  Vanessa Burns
                </span>
              </Typography>
            </Box> */}
            {appSetting ? (
              <Box display="flex">
                <WebAssetIcon fontSize="medium" sx={{ color: colors.black }} />
                <StyledLink
                  href={`${appSetting.website}/car/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {translation.previewLink}
                </StyledLink>
              </Box>
            ) : null}
            <Box display="flex" justifyContent="flex-start">
              <Box marginLeft="2rem">
                <CertifiedTag certified={certified} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </VehicleBox>
  )
}

export default VehicleSummary
